import { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Users,
  GraduationCap,
  Building,
  Briefcase,
  Calendar,
  Clock,
} from "lucide-react";
import { supabase } from "../../integrations/supabase/client";
import { useAuth } from "../../contexts/AuthContext";

interface Institution {
  id: string;
  name: string;
  type: string | null;
  city: string | null;
  state: string | null;
  total_students: number | null;
  staff_count: number | null;
  departments: string[] | null;
  status: string | null;
  created_at: string | null;
}

export default function Dashboard() {
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchInstitution = useCallback(async () => {
    if (!user) {
      return;
    }
    try {
      const { data, error } = await supabase
        .from("institutions")
        .select("*")
        .eq("admin_id", user.id)
        .maybeSingle();

      if (error && error.code !== "PGRST116") {
        throw error;
      }
      setInstitution(data);
    } catch (error) {
      console.error("Error fetching institution:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchInstitution();
    }
  }, [user, fetchInstitution]);

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="border-green-300 bg-green-100 text-green-800">
            Approved
          </Badge>
        );
      case "pending":
        return (
          <Badge className="border-yellow-300 bg-yellow-100 text-yellow-800">
            Pending Approval
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="border-red-300 bg-red-100 text-red-800">
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Institution Dashboard
          </h1>
          <p className="text-muted-foreground">No institution registered yet</p>
        </div>
        <Card className="py-12 text-center">
          <CardContent>
            <Building className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
            <h3 className="mb-2 text-xl font-semibold">No Institution Found</h3>
            <p className="mb-4 text-muted-foreground">
              Please complete your institution registration.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {institution.name}
          </h1>
          <p className="text-muted-foreground">Institution Dashboard</p>
        </div>
        {getStatusBadge(institution.status)}
      </div>

      {institution.status === "pending" && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">
                  Application Under Review
                </p>
                <p className="text-sm text-yellow-600">
                  Your institution registration is pending approval from the
                  admin.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-primary/20 transition-shadow hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Students
            </CardTitle>
            <Users className="h-5 w-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {institution.total_students ?? 0}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Enrolled students
            </p>
          </CardContent>
        </Card>

        <Card className="border-purple-500/20 transition-shadow hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Staff Count
            </CardTitle>
            <Briefcase className="h-5 w-5 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-500">
              {institution.staff_count ?? 0}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Teaching & Non-teaching
            </p>
          </CardContent>
        </Card>

        <Card className="border-orange-500/20 transition-shadow hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Departments
            </CardTitle>
            <Building className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-500">
              {institution.departments?.length ?? 0}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Active departments
            </p>
          </CardContent>
        </Card>

        <Card className="border-blue-500/20 transition-shadow hover:shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Type
            </CardTitle>
            <GraduationCap className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold capitalize text-blue-500">
              {institution.type ?? "N/A"}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {institution.city}, {institution.state}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Departments List */}
      {institution.departments && institution.departments.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Departments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {institution.departments.map(dept => (
                <Badge key={dept} variant="secondary" className="px-3 py-1">
                  {dept}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div className="cursor-pointer rounded-lg bg-muted/50 p-4 text-center transition-colors hover:bg-muted">
              <Users className="mx-auto mb-2 h-8 w-8 text-primary" />
              <p className="text-sm font-medium">Manage Students</p>
            </div>
            <div className="cursor-pointer rounded-lg bg-muted/50 p-4 text-center transition-colors hover:bg-muted">
              <Briefcase className="mx-auto mb-2 h-8 w-8 text-purple-500" />
              <p className="text-sm font-medium">Manage Staff</p>
            </div>
            <div className="cursor-pointer rounded-lg bg-muted/50 p-4 text-center transition-colors hover:bg-muted">
              <Calendar className="mx-auto mb-2 h-8 w-8 text-orange-500" />
              <p className="text-sm font-medium">Attendance</p>
            </div>
            <div className="cursor-pointer rounded-lg bg-muted/50 p-4 text-center transition-colors hover:bg-muted">
              <Building className="mx-auto mb-2 h-8 w-8 text-blue-500" />
              <p className="text-sm font-medium">Reports</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
